import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { NextIcon } from '@kvdbil/components';
import styled from 'styled-components';
import { SelectOption } from '~/App/shared/interfaces/SelectOption';
import { formatMoney } from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import PriceTextFieldWithControl from '../Fields/PriceTextFieldWithControl';
import SelectWithControl from '../Fields/SelectWithControl';
import FinanceMonthlyCost from './FinanceMonthlyCost';
import Link from '~/App/shared/components/Link';

const Container = styled.div`
  padding-top: 1rem;
`;

const Amount = styled.div`
  font-weight: 500;
  font-size: 1.125em;
  text-align: center;
  padding: 1em 0;
  display: block;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
`;

const LinkContainer = styled.div`
  text-align: center;
  padding-top: 1rem;
`;

const LoanInformation = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
`;

interface Form {
  price: number;
  leasingPeriod: SelectOption;
}

interface Props {
  buttonText: string;
  buttonUrl: string;
  costSuffix: string;
  footNote: string;
  isBuyNow?: boolean;
  leasingPeriod?: SelectOption;
  price?: number;
  withResidualValue?: boolean;
}

const getLoanPeriodOptions = (shouldCalculateResidualLoan: boolean) =>
  shouldCalculateResidualLoan
    ? [
        { value: '24', label: '24' },
        { value: '36', label: '36' }
      ]
    : [
        { value: '24', label: '24' },
        { value: '36', label: '36' },
        { value: '48', label: '48' },
        { value: '60', label: '60' },
        { value: '72', label: '72' }
      ];

const Form = styled.form`
  && {
    margin-top: 1rem;
    & > div {
      margin-bottom: 2rem;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const FinanceCalculatorForm = ({
  buttonText,
  buttonUrl,
  costSuffix,
  footNote,
  isBuyNow,
  leasingPeriod,
  price = 0,
  withResidualValue = false
}: Props) => {
  const loanOptions = getLoanPeriodOptions(withResidualValue);
  const { t } = useTranslation();

  const { control } = useForm<Form>({
    defaultValues: {
      price,
      leasingPeriod: leasingPeriod ?? loanOptions.slice(-1)[0]
    }
  });

  const currentPrice = useWatch({
    control,
    name: 'price'
  });

  const currentLeasingPeriod = useWatch({
    control,
    name: 'leasingPeriod'
  });

  return (
    <Container>
      <Form noValidate>
        <PriceTextFieldWithControl
          control={control}
          name="price"
          isDisabled={isBuyNow}
        />

        <SelectWithControl
          control={control}
          name="leasingPeriod"
          label={t('Loan period')}
          options={loanOptions}
        />
      </Form>

      <FinanceMonthlyCost
        price={currentPrice}
        withResidualValue={withResidualValue}
        months={Number(currentLeasingPeriod.value)}
      >
        {({ value }) => (
          <>
            <Amount aria-label="Monthly cost">
              {value
                ? `${t('Monthly cost')}: ${formatMoney({
                    value,
                    currency: costSuffix
                  })}*`
                : '--'}
            </Amount>
          </>
        )}
      </FinanceMonthlyCost>

      {withResidualValue ? (
        <LoanInformation>
          {t(
            '* The monthly cost is calculated based on loan with residual value'
          )}
        </LoanInformation>
      ) : (
        <LoanInformation>* {footNote}</LoanInformation>
      )}

      <LinkContainer>
        <StyledLink to={buttonUrl} target="_blank">
          {buttonText}
          <NextIcon />
        </StyledLink>
      </LinkContainer>
    </Container>
  );
};

export default FinanceCalculatorForm;
